/**
 * 萨米板棋AI逻辑 - 极简优化版
 * 专注性能和响应速度
 */

import {
  TablutGameState,
  TablutMove,
  Position,
  TablutPiece,
  Difficulty,
  GameMode
} from '../types/game';
import {
  getAllPossibleMoves,
  checkGameEnd,
  THRONE,
  CORNERS
} from './tablut';

// 方向向量
const DIRECTIONS = [
  { row: -1, col: 0 }, { row: 1, col: 0 },
  { row: 0, col: -1 }, { row: 0, col: 1 }
];

// 快速查找国王位置
const findKingPosition = (board: (TablutPiece | null)[][]): Position | null => {
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = board[row][col];
      if (piece?.type === 'king') {
        return { row, col };
      }
    }
  }
  return null;
};

// 计算曼哈顿距离
const manhattanDistance = (pos1: Position, pos2: Position): number => {
  return Math.abs(pos1.row - pos2.row) + Math.abs(pos1.col - pos2.col);
};

// 检查位置是否有效
const isValidPos = (pos: Position): boolean => {
  return pos.row >= 0 && pos.row < 9 && pos.col >= 0 && pos.col < 9;
};

// 检查是否为角落
const isCorner = (pos: Position): boolean => {
  return CORNERS.some(corner => corner.row === pos.row && corner.col === pos.col);
};

// 检查是否为中心区域
const isCenter = (pos: Position): boolean => {
  return pos.row >= 3 && pos.row <= 5 && pos.col >= 3 && pos.col <= 5;
};

// 简化的吃子检查（只检查直接夹吃）
const canCaptureDirect = (
  board: (TablutPiece | null)[][],
  movePos: Position,
  player: 'defender' | 'attacker'
): number => {
  let captureCount = 0;

  for (const dir of DIRECTIONS) {
    const enemyPos = {
      row: movePos.row + dir.row,
      col: movePos.col + dir.col
    };
    const capturePos = {
      row: enemyPos.row + dir.row,
      col: enemyPos.col + dir.col
    };

    if (isValidPos(enemyPos) && isValidPos(capturePos)) {
      const enemyPiece = board[enemyPos.row][enemyPos.col];
      const capturePiece = board[capturePos.row][capturePos.col];

      // 检查是否形成夹击：移动棋子 - 敌方棋子 - 己方棋子
      if (enemyPiece && enemyPiece.player !== player &&
          capturePiece && capturePiece.player === player) {

        // 国王特殊规则：需要四面包围
        if (enemyPiece.type === 'king') {
          // 简化检查：如果能够夹住国王，就认为可以捕获
          captureCount++;
        } else {
          // 普通棋子直接夹住即可
          captureCount++;
        }
      }
    }
  }

  return captureCount;
};

// 极简的移动评分
const scoreMoveSimple = (
  gameState: TablutGameState,
  move: TablutMove,
  player: 'defender' | 'attacker'
): number => {
  const piece = gameState.board[move.from.row][move.from.col];
  if (!piece) return 0;

  let score = Math.random() * 10;

  // 1. 国王获胜检查
  if (piece.type === 'king' && isCorner(move.to)) {
    return 100000; // 必胜移动
  }

  // 2. 吃子奖励
  const captureCount = canCaptureDirect(gameState.board, move.to, player);
  score += captureCount * 100;

  // 3. 位置价值
  if (isCorner(move.to)) {
    score += player === 'defender' && piece.type === 'king' ? 1000 : 50;
  } else if (isCenter(move.to)) {
    score += 30;
  }

  // 4. 策略评分
  const kingPos = findKingPosition(gameState.board);
  if (kingPos) {
    const distanceToKing = manhattanDistance(move.to, kingPos);

    if (player === 'defender') {
      // 护王方：靠近国王
      if (piece.type !== 'king') {
        score += Math.max(0, (5 - distanceToKing) * 20);
      }
    } else {
      // 攻击方：接近国王
      if (piece.type === 'attacker') {
        score += Math.max(0, (8 - distanceToKing) * 15);
      }
    }
  }

  return score;
};

// 检查危险移动（简化版）
const isDangerousMoveSimple = (gameState: TablutGameState, move: TablutMove): boolean => {
  const piece = gameState.board[move.from.row][move.from.col];
  if (!piece) return false;

  // 国王移动安全性检查
  if (piece.type === 'king') {
    // 简单检查：如果移动到边缘，可能比较危险
    if (move.to.row === 0 || move.to.row === 8 || move.to.col === 0 || move.to.col === 8) {
      // 如果不是角落，边缘移动可能危险
      if (!isCorner(move.to)) {
        return Math.random() > 0.7; // 30%概率认为是危险
      }
    }
  }

  return false;
};

// 主要AI决策函数（极简版）
export const getTablutAIDecision = (
  gameState: TablutGameState,
  difficulty: Difficulty = 'medium'
): TablutMove | null => {
  const moves = getAllPossibleMoves(gameState, gameState.currentPlayer);

  if (moves.length === 0) {
    return null;
  }

  // 检查游戏是否已经结束
  const gameEnd = checkGameEnd(gameState);
  if (gameEnd.isEnd) {
    return null;
  }

  // 根据难度选择策略
  switch (difficulty) {
    case 'easy':
      return getEasyMoveSimple(gameState, moves);
    case 'medium':
      return getMediumMoveSimple(gameState, moves);
    case 'hard':
      return getHardMoveSimple(gameState, moves);
    default:
      return getMediumMoveSimple(gameState, moves);
  }
};

// 简单模式（极简版）
const getEasyMoveSimple = (gameState: TablutGameState, moves: TablutMove[]): TablutMove => {
  // 随机选择，偏向中心移动
  const scoredMoves = moves.map(move => ({
    move,
    score: Math.random() * 100 + (isCenter(move.to) ? 50 : 0)
  }));

  scoredMoves.sort((a, b) => b.score - a.score);

  // 在前5个最佳移动中随机选择
  const topMoves = scoredMoves.slice(0, Math.min(5, scoredMoves.length));
  return topMoves[Math.floor(Math.random() * topMoves.length)].move;
};

// 中等模式（极简版）
const getMediumMoveSimple = (gameState: TablutGameState, moves: TablutMove[]): TablutMove => {
  // 快速评分所有移动
  const scoredMoves = moves.map(move => ({
    move,
    score: scoreMoveSimple(gameState, move, gameState.currentPlayer)
  }));

  scoredMoves.sort((a, b) => b.score - a.score);

  // 选择最佳移动
  return scoredMoves[0].move;
};

// 困难模式（极简版）
const getHardMoveSimple = (gameState: TablutGameState, moves: TablutMove[]): TablutMove => {
  // 评分并过滤危险移动
  const scoredMoves = moves
    .filter(move => !isDangerousMoveSimple(gameState, move))
    .map(move => ({
      move,
      score: scoreMoveSimple(gameState, move, gameState.currentPlayer)
    }));

  // 如果过滤后没有移动，使用所有移动
  const finalMoves = scoredMoves.length > 0 ? scoredMoves :
    moves.map(move => ({
      move,
      score: scoreMoveSimple(gameState, move, gameState.currentPlayer)
    }));

  finalMoves.sort((a, b) => b.score - a.score);

  // 选择最佳移动
  return finalMoves[0].move;
};

// 极简的AI思考时间
export const simulateTablutAIThinking = async (difficulty: Difficulty = 'medium'): Promise<void> => {
  let thinkingTime: number;

  switch (difficulty) {
    case 'easy':
      thinkingTime = 100 + Math.random() * 200; // 100-300ms
      break;
    case 'medium':
      thinkingTime = 200 + Math.random() * 300; // 200-500ms
      break;
    case 'hard':
      thinkingTime = 300 + Math.random() * 200; // 300-500ms
      break;
    default:
      thinkingTime = 300;
  }

  await new Promise(resolve => setTimeout(resolve, thinkingTime));
};

export default {
  getTablutAIDecision,
  simulateTablutAIThinking
};